import { Stack, Typography } from "@mui/material";
import { AlertCircle } from "lucide-react";
import {
  Dialog,
  FoundationColorTokens,
  Spacing,
  Typography as LeoTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { getIconProps, ICON_SIZE } from "@pulse/shared-components";
import { TFunction } from "i18next";
import { observer } from "mobx-react";
import { Instance } from "mobx-state-tree";
import React from "react";
import { DialogErrorContent } from "../../../../components/DialogErrorContent";
import {
  DeleteSurveyLanguageError,
  TranslationsStore,
} from "../../store/TranslationsStore";

interface DeleteSurveyLanguageDialogProps {
  open: boolean;
  languageCode: string;
  surveyId: string;
  projectId: string;
  languageId: string;
  translationsStore: Instance<typeof TranslationsStore>;
  t: TFunction;
  spacing: Spacing;
  typography: LeoTypography;
  tokens: FoundationColorTokens<string>;
  onClose: VoidFunction;
  onSuccess: () => Promise<void>;
}

export const DeleteSurveyLanguageDialog = observer(
  ({
    open,
    languageCode,
    surveyId,
    projectId,
    languageId,
    translationsStore,
    t,
    spacing,
    typography,
    tokens,
    onClose,
    onSuccess,
  }: DeleteSurveyLanguageDialogProps): React.ReactElement => {
    const hasError = translationsStore.deleteSurveyLanguageError !== null;

    const getErrorText = (): string => {
      switch (translationsStore.deleteSurveyLanguageError) {
        case DeleteSurveyLanguageError.LanguageInUse:
          return t("surveys.translations.deleteDialog.languageInUseError");
        case DeleteSurveyLanguageError.ProjectAlreadyArchived:
          return t("surveys.translations.languageAction.projectArchivedError");
        case DeleteSurveyLanguageError.TranslationUpdateInProgress:
          return t(
            "surveys.translations.languageAction.translationUpdateInProgressError",
          );
        default:
          return t("surveys.translations.languageAction.unexpectedError");
      }
    };

    const closeDialog = (): void => {
      translationsStore.resetDeleteSurveyLanguageState();
      onClose();
    };

    return (
      <Dialog
        open={open}
        title={t("surveys.translations.deleteDialog.title")}
        primaryButtonText={
          hasError ? undefined : t("surveys.translations.deleteDialog.title")
        }
        primaryButtonColor="destructive"
        onPrimaryButtonClick={async () => {
          if (translationsStore.isDeletingSurveyLanguage) {
            return;
          }
          await translationsStore.deleteSurveyLanguage(
            surveyId,
            projectId,
            languageId,
          );
          if (translationsStore.deleteSurveyLanguageError === null) {
            await onSuccess();
            closeDialog();
          }
        }}
        secondaryButtonText={hasError ? t("common.close") : t("common.cancel")}
        onSecondaryButtonClick={closeDialog}
        isSecondaryButtonDisabled={translationsStore.isDeletingSurveyLanguage}
        disableBackdropClick={true}
        contentPadding={spacing.spaceLG}
        width="560px"
      >
        {hasError ? (
          <DialogErrorContent
            spacing={spacing}
            tokens={tokens}
            typography={typography}
            errorText={getErrorText()}
          />
        ) : (
          <Stack direction="row" gap={spacing.spaceXS} alignItems="flex-start">
            <AlertCircle
              {...getIconProps(tokens.iconError, ICON_SIZE.default)}
            />
            <Typography {...typography.b2} color={tokens.labelError}>
              {t("surveys.translations.deleteDialog.description", {
                languageCode,
              })}
            </Typography>
          </Stack>
        )}
      </Dialog>
    );
  },
);
